<template>
  <section
    class="table-box mt-5"
    :data-test-id="constants.DATA_TEST_ID_VALUES.HOME_QUOTES_TABLE"
  >
    <Loading :loading="loading" />
    <GlobalEvents @keydown.alt.83="keymap" @keydown.shift.219="keymap" />
    <div class="columns is-flex-mobile">
      <div class="column">
        <h2 class="is-h2 p-1 pb-3"><strong>Recent Itineraries</strong></h2>
      </div>
      <div class="column has-text-right">
        <NewQuoteButton />
      </div>
    </div>
    <QuotesTableSkeleton v-if="loading" :rows="10" />
    <b-table
      v-else
      :data="itinerariesLimited"
      :row-class="helper.getItineraryRowClass"
      hoverable
    >
      <b-table-column
        v-if="isProVersion"
        v-slot="props"
        field="client"
        label="Client"
        width="15%"
      >
        {{ getFieldValidatedData(props.row, "client", "name") }}
      </b-table-column>

      <b-table-column
        v-if="isProVersion"
        v-slot="props"
        field="type"
        label="Type"
        width="10%"
        centered
      >
        <b-tag
          v-if="helper.getItineraryTypeById(props.row.itinerary_type_id)"
          type="is-light"
        >
          {{ helper.getItineraryTypeById(props.row.itinerary_type_id).name }}
        </b-tag>
      </b-table-column>

      <b-table-column v-slot="props" field="code" label="Code">
        <b-navbar-item
          tag="router-link"
          :to="{
            path: constants.PATH_INFO.BASIC_INFO.path,
            query: { itinerary: props.row.id },
          }"
          class="color-secondary cursor quote__link"
          style="cursor: pointer"
        >
          {{ props.row.code }}
        </b-navbar-item>
      </b-table-column>

      <b-table-column
        v-if="isProVersion"
        v-slot="props"
        field="user"
        label="Assignee"
      >
        <b-tooltip :label="props.row.owner.name" type="is-light">
          {{ helper.formatUserName(props.row.owner) || "-" }}
        </b-tooltip>
      </b-table-column>

      <b-table-column v-slot="props" field="title" label="Title">
        <QuoteTitleTableRow :row="props.row" />
      </b-table-column>

      <b-table-column v-slot="props" field="paxs" label="Paxs" centered>
        {{ props.row.num_pax }}
      </b-table-column>

      <b-table-column v-slot="props" field="price" label="Price" numeric>
        {{ props.row.price }}
      </b-table-column>

      <b-table-column
        v-slot="props"
        field="from"
        label="Travel Dates"
        centered
        width="10%"
      >
        {{
          props.row.start_date | customDate(constants.DATE_FORMAT_MMM_DD_YYYY)
        }}
        <br />
        {{ props.row.end_date | customDate(constants.DATE_FORMAT_MMM_DD_YYYY) }}
      </b-table-column>

      <b-table-column
        v-slot="props"
        field="from"
        label="Destinations"
        centered
        width="15%"
      >
        <b-tooltip
          :label="
            globalHelper.getQuoteDestinationsPath(props.row, 'location_name')
          "
          type="is-light"
          multilined
        >
          {{ globalHelper.getQuoteDestinationsPath(props.row) }}
        </b-tooltip>
      </b-table-column>

      <b-table-column
        v-if="authConfig.statusEnabled"
        v-slot="props"
        field="status"
        label="Status"
        width="110"
      >
        <BaseSelect
          v-model="props.row.status.id"
          size="xs"
          :list="props.row.statusesOptions"
          option-value="id"
          option-text="name"
          placeholder="Status"
          :lock="props.row.status.id !== 1"
          :disabled="props.row.status.id !== 1"
          @input="() => setSelectedItinerary(props.row)"
        />
      </b-table-column>

      <b-table-column
        v-slot="props"
        field="edit"
        label="Actions"
        width="6%"
        centered
      >
        <div
          class="is-flex is-justify-content-center is-align-items-center"
          style="gap: 0.5rem"
        >
          <a class="ml-2" @click.prevent="handleEditItinerary(props.row)"
            ><IconPencil class="icon color-4 is-small"
          /></a>
          <QuotesTableActions :data="props.row" />
        </div>
      </b-table-column>
      <template #empty>
        <div class="content has-text-centered py-5">
          <p>
            There are no results. Please try the search again in a couple of
            minutes. If the issue continues, contact support
          </p>
        </div>
      </template>
    </b-table>
    <div class="shortcutLinkContainer">
      <BaseShortcutLink class="shortcutsLink" @click="handleModal" />
      <BaseTourGuideLink :handle-click="openGuideModal" class="shortcutsLink" />
      <BaseModal
        :is-component-modal-active="shortcutsOpen"
        type="shortcuts"
        :shortcuts="shortcuts"
        :close-modal="handleModal"
      >
        <span slot="title">Recent Itineraries keyboard shortcuts </span>
      </BaseModal>
    </div>
    <BaseModal :is-component-modal-active="showStatusChangeModal">
      <IconWarning slot="icon" class="icon is-xsmall color-12" />
      <span slot="title"
        >Are you sure to
        <span class="has-text-weight-semibold">change</span> this status?
      </span>
      <span slot="paragraph"
        >The update will take around 1 minute to become visible</span
      >
      <div slot="buttons" class="is-flex">
        <BaseButton
          action="secondary"
          size="medium"
          :disabled="loading"
          class="mr-4"
          @click="
            () => {
              loadItineraries();
              showStatusChangeModal = false;
            }
          "
        >
          <p slot="text">No, take me back</p>
        </BaseButton>
        <BaseButton
          action="tercery"
          size="medium"
          :disabled="loading"
          @click="() => validateShowExportExternalModal()"
        >
          <p slot="text">Yes, continue</p>
        </BaseButton>
      </div>
    </BaseModal>

    <ExternalExportModal
      :is-modal-open="showExternalExportModal"
      :loading="loading"
      :on-external-export-continue="onExternalExportContinue"
      :close-modal="closeChangeStatusModal"
    />
  </section>
</template>

<script>
import NewQuoteButton from "@/components/_shared/NewQuoteButton.vue";
import ExternalExportModal from "@/components/quote-views/_shared/ExternalExportModal.vue";
import QuotesTableActions from "@/components/_shared/QuotesTableActions.vue";
import QuotesTableSkeleton from "@/components/_shared/QuotesTableSkeleton.vue";
import Loading from "@/components/_shared/Loading.vue";
import { mapGetters } from "vuex";
import globalHelper from "@/helpers";
import GlobalEvents from "vue-global-events";
import { homeQuoteTableShortcuts as shortcuts } from "@/utils/shortcuts";
import {
  ITINERARY_TYPES,
  ITINERARY_STATUS_TO_DISABLE_QUOTE,
  ITINERARY_STATUS_TO_DISABLE_QUOTE_FOR_TRAVEL_AGENTS,
} from "@/constants/itinerary";
import { PATH_INFO } from "@/router/path";
import { DATA_TEST_ID_VALUES } from "@/constants/test";
import { DATE_FORMAT_MMM_DD_YYYY, USER_ROLE } from "@/constants";
import { helper } from "@/utils/itinerary";
import { utilsHelper } from "@/utils/utils";
import { ITINERARY_STATUS } from "@/constants/itinerary";
import QuoteTitleTableRow from "@/components/_shared/QuoteTitleTableRow.vue";

export default {
  name: "HomeQuoteTable",
  components: {
    QuoteTitleTableRow,
    NewQuoteButton,
    QuotesTableActions,
    GlobalEvents,
    Loading,
    QuotesTableSkeleton,
    ExternalExportModal,
  },
  props: {
    openGuideModal: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      constants: { DATA_TEST_ID_VALUES, DATE_FORMAT_MMM_DD_YYYY, PATH_INFO },
      helper: { ...helper, ...utilsHelper },
      globalHelper: globalHelper,
      showStatusChangeModal: false,
      showExternalExportModal: false,
      shortcutsOpen: false,
      shortcuts,
      loading: null,
      selectedItinerary: {
        id: null,
        statusId: null,
        owner: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      itineraries: "itinerary/getItineraries",
      isProVersion: "auth/isProVersion",
      authConfig: "auth/authConfig",
      status_itinerary: "status_itinerary/getStatusesItinerary",
      isEnabledByTenant: "itinerary/getIsEnabledByTenant",
    }),
    itinerariesLimited() {
      return this.itineraries.data;
    },
    info() {
      return {
        itineraries: this.itineraries.data,
      };
    },
  },
  mounted() {
    this.fetchingOnMounted();
  },
  methods: {
    disableStatusColumn(quote) {
      const isTravelAgent = globalHelper.hasAnyRoles(
        this.authData.user,
        [USER_ROLE.TRAVEL_AGENT],
        false
      );

      const itineraryStatusesToDisableQuote = isTravelAgent
        ? ITINERARY_STATUS_TO_DISABLE_QUOTE_FOR_TRAVEL_AGENTS
        : ITINERARY_STATUS_TO_DISABLE_QUOTE;

      return (
        ITINERARY_TYPES.NO_TEMPLATE.id !== quote.itinerary_type_id ||
        itineraryStatusesToDisableQuote.includes(quote.status.id)
      );
    },
    openItineraryInAnotherTab(itinerary = {}) {
      const urlToRedirect = `${window.location.origin}${PATH_INFO.BASIC_INFO.path}?itinerary=${itinerary.id}`;
      window.open(urlToRedirect, "_blank", "");
    },
    getFieldValidatedData(row = {}, key = "", subKey = "") {
      const value = row[key] && row[key][subKey] ? row[key][subKey] : "-";
      return value;
    },
    toggleLoading() {
      if (!this.loading) this.loading = true;
      else this.loading = null;
      this.$store.dispatch("utils/setLoading", this.loading != null);
    },
    handleModal() {
      this.shortcutsOpen = !this.shortcutsOpen;
    },
    keymap(e) {
      e = e || window.event;
      const code = e.which || e.keyCode;
      switch (code) {
        case 83:
        case 219:
          e.preventDefault();
          e.stopPropagation();
          break;
      }
      if (code === 83) {
        this.$router.push(PATH_INFO.QUOTES.path);
        document.getElementById("searchInput").focus();
      } else if (code === 219) {
        this.handleModal();
      }
    },
    async loadItineraries() {
      const payload = {
        page: 1,
        limit: 10,
        type: ITINERARY_TYPES.NO_TEMPLATE.id,
      };

      await this.$store
        .dispatch("itinerary/list", payload)
        .then()
        .catch((error) => {
          throw error;
        });
    },
    async loadStatusesItinerary() {
      const payload = {
        page: 1,
      };

      await this.$store
        .dispatch("status_itinerary/getStatusesItinerary", payload)
        .then()
        .catch((error) => {
          throw error;
        });
    },
    setSelectedItinerary(item) {
      this.selectedItinerary = {
        id: item.id,
        statusId: item.status.id,
        owner: item.owner,
      };
      this.showStatusChangeModal = true;
    },
    resetSelectedItinerary() {
      this.selectedItinerary = {
        id: null,
        statusId: null,
        owner: null,
      };
    },
    handleChangeStatus(externalSource = "") {
      const payload = {
        id: this.selectedItinerary.id,
        status: this.selectedItinerary.statusId,
        source: externalSource,
      };
      this.toggleLoading();
      this.$store
        .dispatch("itinerary/change_status", payload)
        .then((response) => {
          this.showStatusChangeModal = false;
          this.$buefy.toast.open({
            duration: 5000,
            message: response.message,
            position: "is-top",
            type: "is-success",
          });
          this.loadItineraries();
        })
        .catch((error) => {
          this.$buefy.toast.open({
            duration: 5000,
            message:
              "Error trying to change status of itinerary, please try again.",
            position: "is-top",
            type: "is-danger",
          });
          this.showStatusChangeModal = false;
          throw error;
        })
        .finally(() => {
          this.resetSelectedItinerary();
          this.toggleLoading();
        });
    },
    closeChangeStatusModal() {
      this.loadItineraries();
      this.showStatusChangeModal = false;
      this.showExternalExportModal = false;
    },
    hasAccountOnOtherSystem() {
      return this.selectedItinerary?.owner?.external_code?.length > 0;
    },
    async validateShowExportExternalModal() {
      if (
        this.selectedItinerary.statusId === ITINERARY_STATUS.CONFIRM.value &&
        this.hasAccountOnOtherSystem()
      ) {
        this.showStatusChangeModal = false;
        this.showExternalExportModal = true;
        return;
      }
      this.handleChangeStatus();
    },
    async onExternalExportContinue(externalSource = "") {
      this.closeChangeStatusModal();
      await this.$store.dispatch("itinerary/setIsRecentlyExported", true);
      await this.handleChangeStatus(externalSource);
      await this.$store.dispatch("itinerary/setIsRecentlyExported", false);
    },
    handleEditItinerary(row) {
      this.$store.dispatch("itinerary/read", { uuid: row.id }).then(() => {
        this.$router.push(PATH_INFO.BASIC_INFO.path);
      });
    },
    async fetchingOnMounted() {
      this.toggleLoading();
      Promise.all([
        this.loadItineraries(),
        this.loadStatusesItinerary(),
      ]).finally(() => {
        this.toggleLoading();
      });
    },
  },
};
</script>
<style scoped>
:is(.quote__link.navbar-item, .quote__link.navbar-item:hover, .quote__link.navbar-item:active, .quote__link.navbar-item:focus) {
  cursor: pointer;
  color: #f00b81 !important;
  background-color: transparent !important;
}
</style>
